import React, { useState, useContext, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ThemeContext } from '../context/ThemeContext';
import { LOCALHOST_PWA_BANNER_CSS_VAR } from '../utils/pwa';

const MarketingLayout = ({ children, showNavbar = true }) => {
  const { modoOscuro } = useContext(ThemeContext);
  const location = useLocation();
  const navbarRef = useRef(null);

  // Detectar página actual para mostrar dots correspondientes
  const isLandingPage = location.pathname === '/';
  const isFuncionalidades = location.pathname === '/funcionalidades';
  const isQuienesSomos = location.pathname === '/quienes-somos';
  const showDots = isLandingPage || isFuncionalidades || isQuienesSomos;

  // Leer sección activa de cada página
  const [activeSection, setActiveSection] = useState(0);
  const sectionCount = isLandingPage ? 4 : isFuncionalidades ? 4 : isQuienesSomos ? 3 : 0;

  useEffect(() => {
    setActiveSection(0);
  }, [location.pathname]);

  useEffect(() => {
    if (!showDots) return;
    
    const interval = setInterval(() => {
      if (isLandingPage) {
        setActiveSection(window.__landingActiveSection || 0);
      } else if (isFuncionalidades) {
        setActiveSection(window.__funcionalidadesActiveSection || 0);
      } else if (isQuienesSomos) {
        setActiveSection(window.__quienesSomosActiveSection || 0);
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [isLandingPage, isFuncionalidades, isQuienesSomos, showDots]);

  const handleDotClick = useCallback((index) => {
    if (isLandingPage && window.__landingScroll) {
      window.__landingScroll.goToSection(index);
    } else if (isFuncionalidades && window.__funcionalidadesScroll) {
      window.__funcionalidadesScroll.goToSection(index);
    } else if (isQuienesSomos && window.__quienesSomosScroll) {
      window.__quienesSomosScroll.goToSection(index);
    }
  }, [isLandingPage, isFuncionalidades, isQuienesSomos]);

  // Estilos para dots
  const dotActive = modoOscuro ? 'bg-white' : 'bg-gray-900';
  const dotInactive = modoOscuro ? 'bg-white/30' : 'bg-gray-400';
  
  // El wrapper ya no tiene fondo - el fondo lo maneja cada página/hero
  const wrapperClass = modoOscuro
    ? 'bg-transparent text-white'
    : 'bg-transparent text-gray-900';

  const navbarVisible = showNavbar;
  const navbarFades = isLandingPage || isFuncionalidades || isQuienesSomos;
  const bannerTop = `var(${LOCALHOST_PWA_BANNER_CSS_VAR}, 0px)`;
  const navbarStyle = {
    top: bannerTop,
    transform: navbarVisible ? 'translateY(0)' : 'translateY(-12px)',
    opacity: navbarVisible ? 1 : 0,
    pointerEvents: navbarVisible ? 'auto' : 'none',
    transition: navbarFades
      ? 'opacity 0.45s ease, transform 0.45s ease'
      : 'none',
  };

  // Reiniciar scroll al cambiar de ruta - usar useLayoutEffect para ejecutar ANTES de la pintura
  useLayoutEffect(() => {
    const container = document.getElementById('main-scroll');
    if (container) {
      container.scrollTo({ top: 0, behavior: 'auto' });
      container.scrollTop = 0;
    }
  }, [location.pathname]);

  // Detectar si hay un modal abierto
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  useEffect(() => {
    const checkModalOpen = () => {
      setIsModalOpen(document.body.classList.contains('modal-open'));
    };
    
    // Verificar inicialmente
    checkModalOpen();
    
    // Usar MutationObserver para detectar cambios en las clases del body
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target === document.body && mutation.attributeName === 'class') {
          checkModalOpen();
        }
      });
    });
    
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  return (
    <div className={`relative min-h-0 w-full max-w-full overflow-x-clip ${wrapperClass}`}>
      <div className="relative z-10 w-full bg-transparent">
        <div ref={navbarRef} className="fixed top-0 left-0 right-0 z-40 bg-transparent" style={navbarStyle}>
          <Navbar />
        </div>
        <main
          className="w-full max-w-full overflow-y-auto overflow-x-clip h-[100dvh] relative z-30"
          id="main-scroll"
        >
          {children}
          <Footer />
        </main>

        {/* Dots de navegación lateral - mostrar en páginas con scroll snap (ocultar cuando modal está abierto) */}
        {showDots && sectionCount > 0 && !isModalOpen && (
          <div className="hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 flex-col gap-3 z-50 pointer-events-auto">
            {Array.from({ length: sectionCount }, (_, index) => (
              <button
                key={index}
                onClick={() => handleDotClick(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  activeSection === index ? dotActive : dotInactive
                }`}
                aria-label={`Ir a sección ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MarketingLayout;
